import { useState, useMemo } from "react";
import {
  Container,
  Grid,
  Stack,
  Title,
  Text,
  Anchor,
  NavLink,
  Divider,
  Breadcrumbs,
} from "@mantine/core";
import {
  IconChevronRight,
  IconScale,
  IconBriefcase,
  IconCash,
  IconShieldLock,
  IconShieldCheck,
  IconExternalLink,
  IconTrendingUp,
  IconCertificate,
} from "@tabler/icons-react";
import Footer from "../../components/Footer";
import { AppEmail, AppName } from "../../lib/data";
import "./styles.scss";

const lastUpdated = "April 2, 2026";

export default function Legal() {
  const [activeSection, setActiveSection] = useState("index");

  const legalContent = useMemo(
    () => [
      {
        id: "index",
        label: "Legal Index",
        icon: <IconScale size={18} />,
        title: "VIREL LEGAL AND POLICY INDEX",
        content: (
          <Stack gap="md">
            <Text>
              This page provides access to the legal policies governing the
              VIREL platform.
            </Text>
            <Text>
              VIREL is owned and operated by NAKUZISA GROUP LTD. By accessing or
              using the VIREL platform, users and campaign providers agree to
              comply with the policies listed below.
            </Text>

            <Divider my="sm" />

            <Title order={3}>Core Documents</Title>
            <Stack gap="xs">
              <Text component="div">
                <strong>TERMS OF SERVICE:</strong> The Terms of Service govern
                access to and use of the VIREL platform. This document defines
                user responsibilities, platform rules, and limitations of
                liability.
              </Text>
              <Text component="div">
                <strong>PRIVACY POLICY:</strong> The Privacy Policy explains how
                personal data is collected, used, protected, and shared when
                users access the platform. This policy aligns with global
                standards and the Nigeria Data Protection Act (NDPA 2023).
              </Text>
              <Text component="div">
                <strong>ACCOUNT DELETION:</strong> Instructions on how to
                permanently delete your VIREL account and associated data in
                compliance with mobile app store requirements.
              </Text>
              <Text component="div">
                <strong>ANTI-FRAUD POLICY:</strong> The Anti-Fraud Policy
                describes prohibited behaviors such as automation, click farms,
                bot activity, and multiple account abuse.
              </Text>
            </Stack>

            <Title order={3} mt="md">
              Operational Frameworks
            </Title>
            <Stack gap="xs">
              <Text component="div">
                <strong>CAMPAIGN PROVIDER TERMS:</strong> These terms apply to
                brands, agencies, or organizations that distribute campaigns
                through the platform.
              </Text>
              <Text component="div">
                <strong>USER EARNINGS DISCLAIMER:</strong> This document
                clarifies that participation on the platform does not constitute
                employment or guaranteed income.
              </Text>
              <Text component="div">
                <strong>DATA PROTECTION STATEMENT:</strong> Our commitment to
                data protection and compliance with applicable privacy laws.
              </Text>
            </Stack>
          </Stack>
        ),
      },
      {
        id: "privacy-policy",
        label: "Privacy Policy",
        icon: <IconShieldLock size={18} />,
        title: "VIREL PRIVACY POLICY",
        content: (
          <Stack gap="md">
            <Text>
              At {AppName}, we take your privacy seriously. This Privacy Policy
              describes how we collect, use, and share your personal information
              when you use our mobile application and platform services.
            </Text>

            <Title order={3}>1. Information We Collect</Title>
            <Text>
              We collect information that you provide directly to us, including
              your name, email address, phone number, and wallet address. We
              also collect information automatically when you use the app, such
              as device identifiers (IMEI/App ID/IDFA), IP address, operating
              system version, and platform activity records.
            </Text>

            <Title order={3}>2. How We Use Your Information</Title>
            <Text>
              We use the collected data to provide and maintain the Platform,
              verify task completion, process reward payouts, detect and prevent
              fraud, send important platform updates, and comply with legal
              obligations. We may also use your contact information to send
              promotional updates, which you can opt-out of at any time.
            </Text>

            <Title order={3}>3. Data Sharing and Third Parties</Title>
            <Text>
              <strong>
                We do not sell your personal data to third parties or data
                brokers.
              </strong>{" "}
              We may share information with trusted third-party service
              providers who assist us in operating our platform, such as cloud
              hosting providers, payment processors, and fraud detection
              services. All such third parties are required to maintain the
              confidentiality of your data.
            </Text>

            <Title order={3}>4. Data Retention</Title>
            <Text>
              We retain your personal information for as long as your account is
              active or as needed to provide you services and comply with our
              legal obligations.
            </Text>

            <Title order={3}>5. Account Deletion and Data Removal</Title>
            <Text>
              In compliance with Apple and Google Play policies, you have the
              right to delete your account and associated personal data at any
              time. You can initiate this process directly within the VIREL
              mobile app settings or by visiting our{" "}
              <Anchor href="/delete-account">Account Deletion Page</Anchor>.
              Once requested, your account will be deactivated, and your
              personal data will be permanently removed from our systems within
              30 days, except where retention is required by law.
            </Text>

            <Title order={3}>6. Children's Privacy</Title>
            <Text>
              VIREL is not intended for use by individuals under the age of 18.
              We do not knowingly collect personal information from children.
            </Text>

            <Title order={3}>7. Updates to This Policy</Title>
            <Text>
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new policy on this page
              and updating the "Last Updated" date.
            </Text>
          </Stack>
        ),
      },
      {
        id: "campaign-service",
        label: "Campaign Service",
        icon: <IconBriefcase size={18} />,
        title: "VIREL CAMPAIGN SERVICE AGREEMENT",
        content: (
          <Stack gap="md">
            <Text fs="italic">Brand / Agency Contract</Text>
            <Text>
              This Campaign Service Agreement ("Agreement") governs the use of
              the VIREL platform for campaign distribution. The VIREL platform
              is owned and operated by NAKUZISA GROUP LTD ("Company").
            </Text>

            <Title order={3}>1. Campaign Services</Title>
            <Text>
              VIREL provides a digital participation platform that distributes
              campaign tasks to registered users. Campaign Providers may use the
              Platform to promote products, services, or initiatives through
              structured engagement activities.
            </Text>

            <Title order={3}>2. Campaign Provider Responsibilities</Title>
            <Text>
              The Campaign Provider represents and warrants that all materials,
              instructions, and content submitted for campaigns are lawful, do
              not violate intellectual property rights, do not contain
              misleading claims, and comply with applicable advertising laws.
            </Text>

            <Title order={3}>3. Campaign Performance</Title>
            <Text>
              VIREL distributes tasks to users but does not guarantee specific
              engagement numbers, conversions, or sales results. To the fullest
              extent permitted by law, VIREL shall not be liable for indirect
              damages or business losses resulting from campaign performance.
            </Text>

            <Title order={3}>4. Governing Law</Title>
            <Text>
              This Agreement shall be governed by the laws of the Federal
              Republic of Nigeria.
            </Text>
          </Stack>
        ),
      },
      {
        id: "campaign-funding",
        label: "Campaign Funding",
        icon: <IconCash size={18} />,
        title: "VIREL CAMPAIGN FUNDING AND ESCROW FRAMEWORK",
        content: (
          <Stack gap="md">
            <Title order={3}>1. Campaign Budget Deposit</Title>
            <Text>
              Campaign providers must deposit the full campaign budget prior to
              campaign activation. The budget includes user reward allocations,
              platform service fees, and campaign management costs.
            </Text>

            <Title order={3}>2. Escrow Holding</Title>
            <Text>
              Campaign funds are held in a secure campaign allocation account
              managed by the Platform. Funds are reserved exclusively for
              campaign execution and user reward distribution.
            </Text>

            <Title order={3}>3. User Reward Protection</Title>
            <Text>
              Once a campaign task has been successfully completed and verified,
              the allocated reward amount is reserved for the user and cannot be
              withdrawn by the campaign provider.
            </Text>

            <Title order={3}>4. Refund Conditions</Title>
            <Text>
              Campaign providers may request partial refunds for unused campaign
              funds if campaign tasks remain incomplete. However, funds
              allocated to verified user participation cannot be reversed except
              in cases of confirmed fraud.
            </Text>
          </Stack>
        ),
      },
      {
        id: "data-protection",
        label: "Data Protection",
        icon: <IconShieldLock size={18} />,
        title: "VIREL DATA PROTECTION AND NDPA COMPLIANCE STATEMENT",
        content: (
          <Stack gap="md">
            <Text>
              This statement outlines the commitment of VIREL to responsible
              data protection and compliance with the Nigeria Data Protection
              Act 2023.
            </Text>

            <Title order={3}>1. Personal Data Collected</Title>
            <Text>
              The Platform may collect personal data necessary for operation,
              including name, email, phone number, device identifiers, IP
              address, and transaction data.
            </Text>

            <Title order={3}>2. Purpose of Data Processing</Title>
            <Text>
              Personal data is processed for operating the platform, verifying
              task participation, processing rewards, fraud detection, customer
              support, and legal compliance.
            </Text>

            <Title order={3}>3. Data Security Measures</Title>
            <Text>
              NAKUZISA GROUP LTD implements reasonable security safeguards
              including secure servers, restricted access controls, and data
              encryption where appropriate.
            </Text>

            <Title order={3}>4. User Rights</Title>
            <Text>
              Users may request access to their personal data, correction of
              inaccurate data, or deletion of their account where permitted by
              law via <strong>{AppEmail}</strong>.
            </Text>
          </Stack>
        ),
      },
      {
        id: "fraud-prevention",
        label: "Anti-Fraud Policy",
        icon: <IconShieldCheck size={18} />,
        title: "VIREL FRAUD DETECTION AND MODERATION FRAMEWORK",
        content: (
          <Stack gap="md">
            <Text fs="italic">Platform Integrity Policy</Text>

            <Title order={3}>1. Fraud Risks</Title>
            <Text>
              Potential fraud scenarios include bot activity, multi-account
              farming, click farms, device spoofing, false task proof
              submissions, and coordinated reward exploitation.
            </Text>

            <Title order={3}>2. Monitoring Systems</Title>
            <Text>
              The Platform uses automated detection systems including device
              fingerprinting, IP monitoring, session pattern analysis, and
              behavioral analytics.
            </Text>

            <Title order={3}>3. Fraud Response</Title>
            <Text>
              When suspicious activity is detected, the moderation team may
              temporarily suspend accounts, freeze withdrawals, request identity
              verification, reverse rewards, or permanently ban accounts.
            </Text>

            <Title order={3}>4. Escalation</Title>
            <Text>
              Severe fraud attempts may be escalated to legal review and
              reported to relevant authorities where necessary.
            </Text>
          </Stack>
        ),
      },
      {
        id: "safe-engagement",
        label: "Safe Engagement",
        icon: <IconShieldCheck size={18} />,
        title: "VIREL THIRD-PARTY PLATFORM COMPLIANCE & SAFE ENGAGEMENT",
        content: (
          <Stack gap="md">
            <Title order={3}>1. Prohibition of Artificial Engagement</Title>
            <Text>
              VIREL strictly prohibits automated engagement, bot activity, click
              farms, device emulation, or any other method designed to
              manipulate third-party platform metrics. We promote genuine,
              voluntary human interaction.
            </Text>

            <Title order={3}>2. Genuine Participation and Content</Title>
            <Text>
              Users must provide honest feedback and genuine engagement.
              Campaign providers are prohibited from requiring users to post
              misleading content, false reviews, or deceptive materials on any
              third-party service. Tasks must comply with the advertising
              standards of the jurisdiction in which they are distributed.
            </Text>

            <Title order={3}>3. User Responsibility</Title>
            <Text>
              Users must independently comply with the terms of service and
              policies of any third-party platform they interact with. VIREL is
              not responsible for enforcement actions, account suspensions, or
              content removals taken by external platforms against users or
              providers.
            </Text>

            <Title order={3}>4. Campaign Provider Responsibility</Title>
            <Text>
              Campaign providers must ensure their instructions do not require
              users to violate third-party policies or applicable laws. VIREL
              reserves the right to reject campaigns that promote deceptive
              practices or violate the integrity of external social ecosystems.
            </Text>
          </Stack>
        ),
      },
      {
        id: "participation-disclaimer",
        label: "Platform Disclaimer",
        icon: <IconExternalLink size={18} />,
        title: "VIREL DIGITAL PLATFORM PARTICIPATION DISCLAIMER",
        content: (
          <Stack gap="md">
            <Text fs="italic">External Platform Protection</Text>

            <Title order={3}>1. Platform Role</Title>
            <Text>
              VIREL operates as a digital participation and campaign
              distribution platform, facilitating voluntary engagement between
              users and providers.
            </Text>

            <Title order={3}>2. Third-Party Neutrality</Title>
            <Text>
              VIREL does not host, distribute, or control content on external
              platforms such as social media networks or streaming services.
              Users interact with such services at their own discretion.
            </Text>

            <Title order={3}>3. No Guarantee of Outcomes</Title>
            <Text>
              Actions performed on external services are not guaranteed to
              produce specific results like ranking changes or algorithmic
              promotion.
            </Text>
          </Stack>
        ),
      },
      {
        id: "trust-reward",
        label: "Trust & Reward",
        icon: <IconCertificate size={18} />,
        title: "VIREL USER TRUST AND REWARD TRANSPARENCY STATEMENT",
        content: (
          <Stack gap="md">
            <Title order={3}>1. Reward Verification</Title>
            <Text>
              Rewards are issued only after successful verification of task
              completion by the Platform’s systems. Verification includes
              automated checks, proof submission, and behavioral analysis.
            </Text>

            <Title order={3}>2. Processing Transparency</Title>
            <Text>
              Users are informed through the interface regarding withdrawal
              status: Pending, Processing, Completed, or Rejected (due to
              verification issues).
            </Text>

            <Title order={3}>3. Commitment to Fairness</Title>
            <Text>
              NAKUZISA GROUP LTD is committed to ensuring that verified
              participation is rewarded fairly and consistently in accordance
              with platform policies.
            </Text>
          </Stack>
        ),
      },
      {
        id: "user-earnings",
        label: "User Earnings",
        icon: <IconTrendingUp size={18} />,
        title: "VIREL USER EARNINGS AND PARTICIPATION DISCLAIMER",
        content: (
          <Stack gap="md">
            <Title order={3}>1. Nature of Participation</Title>
            <Text>
              Participation on the platform does not constitute employment,
              contract work, partnership, or any form of guaranteed income
              arrangement with NAKUZISA GROUP LTD.
            </Text>

            <Title order={3}>2. No Guaranteed Earnings</Title>
            <Text>
              The Platform does not guarantee a specific amount of money or
              rewards. Earnings vary by campaign availability, successful task
              verification, and user participation levels.
            </Text>

            <Title order={3}>3. Limitation of Claims</Title>
            <Text>
              By using the Platform, users acknowledge that the Company is not
              liable for perceived lost earnings, expected rewards, or
              unavailable campaign opportunities.
            </Text>
          </Stack>
        ),
      },
    ],
    [],
  );

  const activeDoc = useMemo(
    () =>
      legalContent.find((doc) => doc.id === activeSection) || legalContent[0],
    [activeSection, legalContent],
  );

  return (
    <div className="legal-page">
      <div className="legal-hero">
        <Container size="xl">
          <Breadcrumbs
            separator={<IconChevronRight size={14} />}
            mb="xl"
            className="legal-breadcrumbs"
          >
            <Anchor href="/" className="breadcrumb-link">
              Home
            </Anchor>
            <Anchor href="/legal" className="breadcrumb-link">
              Legal
            </Anchor>
          </Breadcrumbs>

          <Title className="legal-main-title">Legal Documents</Title>
          <Text className="legal-last-updated">
            Last Updated: {lastUpdated}
          </Text>
        </Container>
      </div>

      <Container size="xl" className="legal-body">
        <Grid gutter={40}>
          <Grid.Col span={{ base: 12, md: 4, lg: 3 }}>
            <div className="legal-toc">
              <Title order={4} className="toc-title">
                Table of Contents
              </Title>
              <Stack gap={4}>
                {legalContent.map((doc) => (
                  <NavLink
                    key={doc.id}
                    label={doc.label}
                    leftSection={doc.icon}
                    active={activeSection === doc.id}
                    onClick={() => setActiveSection(doc.id)}
                    className="toc-link"
                    variant="subtle"
                    color="primary"
                  />
                ))}
              </Stack>

              <Divider my="xl" />

              <div className="toc-contact">
                <Text fw={700} size="sm" mb={4}>
                  Contact Support
                </Text>
                <Anchor
                  href={`mailto:${AppEmail}`}
                  size="sm"
                  className="contact-link"
                >
                  {AppEmail}
                </Anchor>
              </div>
            </div>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
            <div className="legal-document">
              <div className="doc-header">
                <Title order={2} className="doc-title">
                  {activeDoc.title}
                </Title>
              </div>

              <div className="doc-content">{activeDoc.content}</div>

              <div className="doc-footer">
                <Text size="sm" c="dimmed">
                  This document is part of the {AppName} Legal Framework. For
                  the full Terms of Service, please visit{" "}
                  <Anchor href="/terms">here</Anchor>.
                </Text>
                <Text size="xs" c="dimmed" mt="xs">
                  © {new Date().getFullYear()} NAKUZISA GROUP LTD. All rights
                  reserved.
                </Text>
              </div>
            </div>
          </Grid.Col>
        </Grid>
      </Container>

      <Footer />
    </div>
  );
}
